"use client";

import { c as _c } from "react/compiler-runtime";
import { Field } from "@base-ui/react/field";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { useActionState, useId, useRef } from "react";
import { submitFeedback } from "@/app/feedback/actions";
import { Button } from "@/components/ui/button";
import { LoadingLabel } from "@/components/ui/loading-label";
import { Textarea } from "@/components/ui/textarea";
import { sleep } from "@/lib/sleep";
import { cn } from "@/lib/utils";
interface FeedbackFormState {
  error?: string;
  message: string;
  success?: boolean;
}
interface FeedbackFormProps {
  compact?: boolean;
  label?: string;
  onSuccess?: () => void;
  placeholder?: string;
}
export function FeedbackForm(t0) {
  const $ = _c(45);
  const {
    compact: t1,
    label,
    onSuccess,
    placeholder: t2
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const placeholder = t2 === undefined ? "Submit your idea, suggestion or feedback\u2026" : t2;
  const errorId = useId();
  const textareaRef = useRef(null);
  let t3;
  if ($[0] !== onSuccess) {
    t3 = async (_previous, formData) => {
      const message = String(formData.get("message") ?? "");
      if (!message.trim()) {
        return {
          error: "Write a message before submitting.",
          message
        };
      }
      try {
        const [result] = await Promise.all([submitFeedback({
          message,
          pageUrl: window.location.pathname
        }), sleep(600)]);
        if (result.error) {
          return {
            error: result.error,
            message
          };
        }
      } catch {
        return {
          error: "Unable to submit. Check your connection and try again.",
          message
        };
      }
      onSuccess?.();
      return {
        message: "",
        success: true
      };
    };
    $[0] = onSuccess;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      message: ""
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [state, formAction, pending] = useActionState(t3, t4);
  const error = pending ? undefined : state.error;
  const t5 = compact ? "flex h-full flex-col gap-2" : "contents";
  const t6 = Boolean(error);
  const t7 = label ? undefined : "Feedback message";
  let t8;
  if ($[3] !== compact || $[4] !== label) {
    t8 = compact ? "min-h-0 flex-1 resize-none sm:text-sm" : cn("resize-none p-3", !label && "min-h-40");
    $[3] = compact;
    $[4] = label;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = compact ? "default" : "secondary";
  let t10;
  if ($[6] !== errorId || $[7] !== pending || $[8] !== placeholder || $[9] !== state.message || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = <Textarea aria-describedby={errorId} aria-invalid={t6} aria-label={t7} className={t8} defaultValue={state.message} disabled={pending} name="message" onKeyDown={_temp} placeholder={placeholder} ref={textareaRef} variant={t9} />;
    $[6] = errorId;
    $[7] = pending;
    $[8] = placeholder;
    $[9] = state.message;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== error || $[16] !== errorId) {
    t11 = error && <p className="text-red-500 text-sm" id={errorId} role="status">{error}</p>;
    $[15] = error;
    $[16] = errorId;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = compact && "w-full shrink-0";
  const t13 = !compact && (label ? "w-40 self-end" : "w-full sm:w-40 sm:self-end");
  let t14;
  if ($[18] !== t12 || $[19] !== t13) {
    t14 = cn("relative overflow-hidden", t12, t13);
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      if (!textareaRef.current?.value.trim()) {
        textareaRef.current?.focus();
      }
    };
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = !compact;
  let t17;
  if ($[22] !== pending) {
    t17 = <LoadingLabel pending={pending} spinnerClassName="text-gray-900">Submit feedback</LoadingLabel>;
    $[22] = pending;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== pending || $[25] !== t14 || $[26] !== t16 || $[27] !== t17) {
    t18 = <Button className={t14} disabled={pending} onClick={t15} rounded={t16} size="md" type="submit" variant="special">{t17}</Button>;
    $[24] = pending;
    $[25] = t14;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t18 || $[32] !== t5) {
    t19 = <form action={formAction} className={t5}>{t10}{t11}{t18}</form>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t18;
    $[32] = t5;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const form = t19;
  if (compact) {
    return form;
  }
  let t20;
  if ($[34] !== state.success) {
    t20 = state.success ? <FeedbackSuccess /> : null;
    $[34] = state.success;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t20) {
    t21 = <div role="status">{t20}</div>;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== form || $[39] !== label || $[40] !== state.success) {
    t22 = !state.success && <Field.Root className={cn("flex flex-col", label ? "gap-6" : "gap-2")}>{label && <Field.Label className="text-balance font-[550] text-base text-gray-1200">{label}</Field.Label>}{form}</Field.Root>;
    $[38] = form;
    $[39] = label;
    $[40] = state.success;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21 || $[43] !== t22) {
    t23 = <>{t21}{t22}</>;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  return t23;
}
function _temp(e) {
  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  }
}
export function FeedbackSuccess() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex size-10 items-center justify-center rounded-full border border-green-500/10 bg-green-500/10"><IconCircleCheck className="size-6 text-green-500" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex h-full flex-col items-center justify-center gap-2">{t0}<div className="flex flex-col items-center justify-center"><span className="font-semibold text-gray-1200">Feedback Sent</span><p className="text-gray-1100 text-sm">Thank you for your feedback!</p></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}